/** Instance-owned lifecycle extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamLifecycle(runtime) {
  runtime.currentModeConfig = function () {
    return runtime.GAME_MODES[runtime.game.mode] || runtime.GAME_MODES.classic;
  };
  runtime.normalizeGameMode = function (mode) {
    return runtime.GAME_MODES[mode] ? mode : 'classic';
  };
  runtime.modeLabel = function (mode = runtime.game.mode) {
    return runtime.GAME_MODES[runtime.normalizeGameMode(mode)].label;
  };
  runtime.getPlayerBallBaseDamage = function (mode = runtime.game.mode) {
    return runtime.PLAYER_BALL_BASE_DAMAGE_BY_MODE[runtime.normalizeGameMode(mode)] || 1;
  };
  runtime.isRapidLaunchMode = function () {
    return Boolean(runtime.currentModeConfig().rapidLaunch);
  };
  runtime.isPressureMode = function () {
    return runtime.game.mode === 'speed' || runtime.game.mode === 'timed';
  };
  runtime.isMobilePressureMode = function () {
    return (
      runtime.MOBILE_QUALITY &&
      runtime.isPressureMode() &&
      runtime.game.modeRunStarted &&
      runtime.game.status === 'playing'
    );
  };
  runtime.currentEnergySettings = function () {
    return runtime.ENERGY_MODES[runtime.game.energyMode] || runtime.ENERGY_MODES.normal;
  };
  runtime.markInteractionBoost = function (durationMs = runtime.ENERGY_INTERACTION_BOOST_MS) {
    runtime.game.interactionBoostUntil = Math.max(
      runtime.game.interactionBoostUntil || 0,
      runtime.now() + durationMs,
    );
  };
  runtime.isInteractionBoostActive = function (now = runtime.now()) {
    return now < (runtime.game.interactionBoostUntil || 0);
  };
  runtime.computeEntityLoad = function () {
    return (
      runtime.game.balls.length +
      runtime.game.launchQueue +
      runtime.game.particles.length * 0.08 +
      runtime.game.bursts.length * 0.4 +
      runtime.game.floaters.length * 0.18 +
      runtime.game.planets.length * 9 +
      runtime.game.walls.length * 0.08 +
      (runtime.game.boardShiftCue > 0 ? 30 : 0) +
      (runtime.game.neutronBombsInDeployment > 0 ? 42 : 0)
    );
  };
  runtime.setEnergyMode = function (mode, reason) {
    if (runtime.game.energyMode === mode && runtime.game.energyReason === reason) return;
    runtime.game.energyMode = mode;
    runtime.game.energyReason = reason;
  };
  runtime.updateEnergyGovernor = function (sampleSeconds, averageFrameMs, observedFps) {
    runtime.game.observedFps = observedFps;
    runtime.game.energyLoad = runtime.computeEntityLoad();
    if (
      document.hidden ||
      runtime.game.status === 'paused' ||
      runtime.ui.rulesModal.classList.contains('is-visible')
    ) {
      runtime.setEnergyMode('idle', 'not actively visible');
      runtime.game.energyPressureSeconds = 0;
      return;
    }
    const mobilePressure = runtime.isMobilePressureMode();
    const baseMode = mobilePressure ? 'balanced' : 'normal';
    const baseReason = mobilePressure ? 'mobile pressure default' : 'normal';
    const lowObservedFps =
      runtime.game.energyMode === 'pressureGuard'
        ? observedFps > 0 && observedFps < 20
        : observedFps > 0 && observedFps < 27;
    const pressured =
      averageFrameMs >= runtime.ENERGY_FRAME_PRESSURE_MS ||
      lowObservedFps ||
      runtime.game.energyLoad >= runtime.ENERGY_PRESSURE_ENTITY_LOAD;
    const pressurePressure =
      averageFrameMs >= runtime.ENERGY_THERMAL_FRAME_MS ||
      runtime.game.energyLoad >= runtime.ENERGY_THERMAL_ENTITY_LOAD ||
      (mobilePressure &&
        runtime.game.boardShiftCue > 0 &&
        runtime.game.balls.length >= runtime.LOW_POWER_BALL_THRESHOLD);
    if (pressurePressure) {
      runtime.game.energyPressureSeconds += sampleSeconds;
      runtime.game.energyRecoverySeconds = 0;
    } else if (pressured) {
      runtime.game.energyPressureSeconds += sampleSeconds * 0.55;
      runtime.game.energyRecoverySeconds = 0;
    } else {
      runtime.game.energyPressureSeconds = Math.max(
        0,
        runtime.game.energyPressureSeconds - sampleSeconds * 0.75,
      );
      runtime.game.energyRecoverySeconds +=
        averageFrameMs <= runtime.ENERGY_RECOVERY_FRAME_MS ? sampleSeconds : 0;
    }
    if (mobilePressure && runtime.game.energyPressureSeconds >= runtime.ENERGY_PRESSURE_SECONDS) {
      runtime.setEnergyMode(
        'pressureGuard',
        pressurePressure ? 'sustained mobile frame pressure' : 'sustained mobile entity load',
      );
      return;
    }
    if (
      runtime.game.energyMode === 'pressureGuard' &&
      runtime.game.energyRecoverySeconds < runtime.ENERGY_RECOVERY_SECONDS
    )
      return;
    runtime.setEnergyMode(baseMode, baseReason);
  };
  runtime.isChaosLaunchActive = function () {
    return runtime.game.chaosTurns > 0 && runtime.game.chaosStacks > 0;
  };
  runtime.markBoardSceneDirty = function (options = {}) {
    runtime.boardSceneDirty = true;
    if (options.shiftCue && runtime.MOBILE_QUALITY && runtime.isPressureMode()) {
      const duration = runtime.game.mode === 'speed' ? 0.16 : 0.2;
      runtime.game.boardShiftCue = duration;
      runtime.game.boardShiftCueMax = duration;
    }
  };
  runtime.shouldCapUserLaunchedBalls = function () {
    return runtime.isRapidLaunchMode() && !runtime.isChaosLaunchActive();
  };
  runtime.syncModeControls = function () {
    runtime.ui.modeButtons.forEach((button) => {
      const active = button.dataset.gameMode === runtime.game.mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (runtime.ui.modeSelect)
      runtime.ui.modeSelect.hidden =
        runtime.game.modeRunStarted || runtime.game.status === 'gameover';
    runtime.ui.scoreboardModeTabs.forEach((button) => {
      const active = button.dataset.boardMode === runtime.game.scoreboardBoardMode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (runtime.ui.autoUpgrade) {
      runtime.ui.autoUpgrade.disabled = Boolean(runtime.currentModeConfig().forceAutoUpgrade);
    }
    const autoUpgradeText = runtime.ui.autoUpgradeLabel?.querySelector('span');
    if (autoUpgradeText)
      autoUpgradeText.textContent = runtime.currentModeConfig().forceAutoUpgrade
        ? 'Auto Locked'
        : 'Auto Upgrade';
  };
  runtime.selectGameMode = function (mode) {
    const nextMode = runtime.normalizeGameMode(mode);
    if (runtime.game.mode === nextMode && !runtime.game.modeRunStarted) {
      runtime.syncModeControls();
      return;
    }
    runtime.game.mode = nextMode;
    runtime.game.scoreboardBoardMode = nextMode;
    runtime.resetGame({ preserveMode: true });
  };
  runtime.resetGame = function (options = {}) {
    const nextMode = options.preserveMode
      ? runtime.normalizeGameMode(runtime.game.mode)
      : 'classic';
    const modeConfig = runtime.GAME_MODES[nextMode];
    Object.assign(runtime.game, {
      mode: nextMode,
      scoreboardBoardMode: nextMode,
      level: 1,
      score: 0,
      coins: 0,
      ballCount: 1,
      crit: 0,
      launchWaitSeconds: runtime.DEFAULT_LAUNCH_WAIT_SECONDS,
      powerupChanceBonus: 0,
      autoUpgrade: Boolean(modeConfig.forceAutoUpgrade),
      angle: 90,
      status: 'ready',
      balls: [],
      bricks: [],
      orbs: [],
      walls: [],
      planets: [],
      particles: [],
      bursts: [],
      floaters: [],
      screenFlash: 0,
      shake: 0,
      boardShiftCue: 0,
      boardShiftCueMax: 0,
      chaosTurns: 0,
      chaosStacks: 0,
      brickBuckets: new Map(),
      queryMark: 1,
      drawQuality: 'high',
      spatialDirty: true,
      launchQueue: 0,
      launchDelay: 0,
      launchSource: 'user',
      volleyStartedAt: 0,
      roundAdvancedForVolley: false,
      nextBallId: 1,
      nextPlanetId: 1,
      volleyActive: false,
      aiming: false,
      lastTick: runtime.now(),
      lastError: '',
      lastMods: '',
      launchFlash: 0,
      lastLaunchIntentAt: 0,
      fps: 0,
      frameMs: 0,
      updateMs: 0,
      drawMs: 0,
      gravityCalls: 0,
      gravityTrapCalls: 0,
      lastFrameTime: 0,
      frameBudgetMs: runtime.FRAME_TARGETS.active,
      perfDebug: runtime.PERF_DEBUG,
      frameSample: { count: 0, elapsed: 0, draw: 0 },
      energyMode: runtime.MOBILE_QUALITY && modeConfig.rapidLaunch ? 'balanced' : 'normal',
      energyReason:
        runtime.MOBILE_QUALITY && modeConfig.rapidLaunch ? 'mobile pressure default' : 'normal',
      energyPressureSeconds: 0,
      energyRecoverySeconds: 0,
      energyLoad: 0,
      observedFps: 0,
      interactionBoostUntil: 0,
      activeUserBalls: 0,
      activeNeutronBombs: 0,
      hudLastSyncAt: 0,
      hudLastFastSyncAt: 0,
      hudSnapshot: {},
      effectQueue: [],
      autoUpgradeRunning: false,
      scoreboardRange: 'all',
      scoreboardMode: 'gameover',
      scoreboardRequestId: 0,
      scoreboardRows: [],
      scoreboardSource: 'web',
      currentRunId: runtime.createRunId(),
      scoreSavedForRun: false,
      boardClearRewarded: false,
      boardClears: 0,
      boardClearHitPower: 0,
      boardClearRefillRows: 0,
      modeRunStarted: false,
      modeTimerRemaining: modeConfig.durationSeconds || 0,
      timedRowAdvanceElapsed: 0,
      timedCurrentRowAdvanceSeconds: runtime.TIMED_ROW_ADVANCE_SECONDS,
      destroyedBlocksSinceAdvance: 0,
      speedNextAdvanceBlocks: runtime.SPEED_INITIAL_ADVANCE_BLOCKS,
      speedQueuedRows: 0,
      speedQueuedRowTimer: 0,
      speedTimedPressureElapsed: 0,
      neutronFuel: 0,
      neutronFuelCost: runtime.NEUTRON_FUEL_INITIAL_COST,
      neutronDeployments: 0,
      neutronBombsInDeployment: 0,
      neutronStarChain: 0,
      neutronDeploymentId: 0,
      neutronPointerArmed: false,
      costs: { balls: 40, impact: 58, launchTime: 70, powerupChance: 92 },
      mods: modeConfig.rapidLaunch
        ? [
            `${modeConfig.label}: tap to keep launching`,
            'Auto Upgrade locked on',
            'Combos carry across launches',
          ]
        : ['Durability climbs every wave', 'Maze rails redirect shots', 'Powerups split volleys'],
    });
    if (runtime.ui.slider) runtime.ui.slider.value = runtime.game.angle;
    if (runtime.ui.autoUpgrade) runtime.ui.autoUpgrade.checked = runtime.game.autoUpgrade;
    runtime.boardSceneReady = false;
    runtime.markBoardSceneDirty();
    runtime.buildOpeningBoard();
    runtime.showOverlay('Ready', modeConfig.overlayTitle, modeConfig.overlayCopy);
    runtime.syncModeControls();
    runtime.syncHud();
  };
}
